// Capability-driven UI gating helpers.
//
// The user object (from /auth/login or /auth/me) carries:
//   - is_super: boolean
//   - permissions: [{ name, description, domain, action }, ...]   // objects, not strings
//
// Gate rule: supers pass every check. Non-supers only pass if they hold the
// matching capability code (permissions[].name). Some actions are SUPER-ONLY
// (no capability exists) — gate those with `isSuper`, not `hasCap`.

// Return the flat list of capability codes a user holds.
export const getUserPermissionNames = (user) => {
  const perms = user?.permissions;
  if (!Array.isArray(perms)) return [];
  return perms
    .map((p) => (typeof p === "string" ? p : p?.name))
    .filter(Boolean);
};

// Does the user hold a specific capability code (ignoring super)?
export const userHasPermission = (user, code) =>
  getUserPermissionNames(user).includes(code);

// Is this a superadmin? Use for SUPER-ONLY actions that have no capability code.
export const isSuper = (user) => !!user?.is_super;

export const isCompanyAdmin = (user) => !!user?.is_admin;

export const isVendor = (user) => !!user?.is_vendor;

export const hasCap = (user, code) => isSuper(user) || userHasPermission(user, code);

// Back-compat alias — existing call sites import `userCanAccess`.
export const userCanAccess = hasCap;

// Who may enter the backoffice shell: super, or anyone holding `dashboard.login`.
export const canAccessBackoffice = (user) => hasCap(user, "dashboard.login");

// Money visibility (financials.view) — a cross-cutting, FRONTEND-ONLY gate that
// decides whether the SPA DISPLAYS monetary figures. Supers always see money.

export const hasMoney = (user) =>
  isSuper(user) || isCompanyAdmin(user) || isVendor(user) || userHasPermission(user, "financials.view");

// Convenience: read the stored login user and check money visibility. Used by
// pure formatters (e.g. moneyValue) that have no user in scope.
export const currentUserHasMoney = () => {
  try {
    return hasMoney(JSON.parse(localStorage.getItem("user")));
  } catch (e) {
    return false;
  }
};


// Placeholder shown in place of a masked monetary value.
export const MONEY_MASK = "•••";
